/**
 * usePermissions — the one place the frontend answers "may this user do X?".
 *
 * Replaces six copy-pasted inline expressions (Plan-RBAC-SchemaRoles-2026-08.md
 * §6.4): `Inbox.tsx`, `HelpCenterStudio.tsx`, `Integrations.tsx`, `Agents.tsx`,
 * `Profile.tsx`, `PlanBanner.tsx` — plus a seventh in `Team.tsx`. Those six were
 * NOT the same expression; they were four distinct policies (see each named
 * getter below), so this hook preserves each one rather than collapsing them.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SOURCE SWITCH — read this before changing anything here.
 * ─────────────────────────────────────────────────────────────────────────────
 * Two constraints are in tension:
 *
 *   (a) `WorkspaceContext.userRole` (per-workspace membership) must become the
 *       source of truth — it is exported today but never consumed, which is why
 *       every check reads the GLOBAL `useAuth().user.roles`.
 *   (b) This de-duplication must not change behavior.
 *
 * They conflict exactly where the two sources disagree — which is the live
 * privilege-escalation bug itself (every self-registered user carries
 * `roles: ["admin"], is_owner: true` on the user doc, globally). Switching
 * sources unconditionally would silently ship a security fix as a refactor.
 *
 * So the switch is gated on the same `rbac_route_guards` hydration flag as the
 * route guards:
 *
 *   flag OFF ⇒ read `user.roles` / `user.is_owner` — byte-identical to today.
 *   flag ON  ⇒ read the active workspace's membership roles.
 *
 * Under the pinned contract the flag-ON fallback is safe too: all five auth
 * responses return `roles`/`is_owner` already RESOLVED for the active
 * workspace, so `user.roles` is workspace-scoped by then anyway. Field names
 * never change, which is what makes this work in both flag states.
 *
 * 🚨 `rolesResolved` is load-bearing. An errored or in-flight members fetch
 * yields `userRole: []`, which is indistinguishable from a genuinely role-less
 * member. Treating that as "no permissions" locks out workspace admins, so we
 * fall back to the global payload whenever membership is unresolved.
 */
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { isNavItemVisible, NAV_ITEMS } from '../config/navigation';
import type { NavItem } from '../config/navigation';
import { getVerbSet } from '../types/rbac';
import type { RolePermissions, Verb } from '../types/rbac';
import type { Workspace } from '../types';

export interface Permissions {
  /** Effective roles for the active workspace (see the source switch above). */
  roles: string[];
  /** Effective owner flag for the active workspace. */
  isOwner: boolean;
  /** Whether the RBAC route guards + per-workspace source switch are live. */
  guardsEnabled: boolean;
  /** True while the effective role set is still settling. */
  permsLoading: boolean;

  /** Admin or owner of the active workspace. */
  isAdminOrOwner: boolean;
  /** May assign conversations to teammates (Inbox). */
  canAssignConversations: boolean;
  /** May create/manage AI agents. */
  canManageAgents: boolean;
  /** May edit a specific workspace from the Profile list — see the note there. */
  canEditWorkspace: (ws: Pick<Workspace, 'owner_id'>) => boolean;

  /** Sidebar/route visibility for a nav item. Mirrors Layout's filter chain. */
  canAccessNavItem: (item: NavItem) => boolean;

  /** Whether per-schema verbs are live (Phase 1) rather than legacy role ids. */
  customRolesEnabled: boolean;
  /** The member's effective verbs, merged across the roles they hold. */
  permissions: RolePermissions;
  /**
   * Phase 1 capability check for intra-page controls (buttons, forms).
   *
   * 🚨 With custom roles OFF this falls back to the LEGACY role list for the
   * schema, so callers behave exactly as before the flag existed. It is
   * therefore safe to call from anywhere today.
   */
  can: (schema: string, verb: Verb) => boolean;
}

export const usePermissions = (): Permissions => {
  const { user, loading: authLoading } = useAuth();
  const {
    userRole,
    isOwner: wsIsOwner,
    rolesResolved,
    membersSettled,
    rolePermissions,
    permissionsReady,
    loading: wsLoading,
  } = useWorkspace();

  // Feature-detect fail-CLOSED: an absent key must read as OFF, never as ON.
  const guardsEnabled = user?.features?.rbac_route_guards === true;

  const globalRoles = user?.roles ?? [];
  const globalIsOwner = user?.is_owner === true;

  const useWorkspaceSource = guardsEnabled && rolesResolved;
  const roles = useWorkspaceSource ? userRole : globalRoles;
  const isOwner = useWorkspaceSource ? wsIsOwner : globalIsOwner;

  // 🚨 Gated on `membersSettled`, NOT `rolesResolved`. A user who is absent
  // from the members list never resolves, so spinning until roles resolve
  // spins forever — they must fall through to the global payload and get a
  // real answer (allowed or NoAccess), never a permanent spinner.
  //
  // Only meaningful when the guards are on: with the flag off nothing reads
  // membership, so callers must not be blocked by that fetch at all.
  const permsLoading = guardsEnabled && (authLoading || wsLoading || !membersSettled);

  const hasAnyRole = (allowed: string[]) => allowed.some((r) => roles.includes(r));

  /**
   * Legacy shape #1 — `Agents.tsx:57`, `PlanBanner.tsx:15`, `Team.tsx`:
   *   `wsIsOwner || user?.is_owner || user?.roles?.includes('admin')`
   * Note it already consulted BOTH owner signals, so the flag-off branch keeps
   * both; the flag-on branch collapses to the single resolved owner flag.
   */
  const isAdminOrOwner = useWorkspaceSource
    ? isOwner || roles.includes('admin')
    : wsIsOwner || globalIsOwner || globalRoles.includes('admin');

  /**
   * Legacy shape #2 — `Inbox.tsx:60`:
   *   `user?.is_owner || user?.roles?.some(r => [...3 roles].includes(r)) || false`
   * The widest of the six: owner OR any of three roles.
   */
  const canAssignConversations = useWorkspaceSource
    ? isOwner || hasAnyRole(['admin', 'tickets_manager', 'agents_manager'])
    : globalIsOwner ||
      globalRoles.some((r) => ['admin', 'tickets_manager', 'agents_manager'].includes(r));

  /**
   * Legacy shape #3 — `HelpCenterStudio.tsx:269`, `Integrations.tsx:636`:
   *   `user?.roles?.some(r => ['admin', 'agents_manager'].includes(r))`
   * 🚨 These two carried NO owner term at all — an owner whose role list lacked
   * `admin` lost the "Create your first agent" CTA. That is invisible today
   * because every self-registered user carries `admin` globally, but it becomes
   * a live regression the moment membership roles are authoritative (an owner's
   * membership doc can legitimately hold `roles: []`). The flag-off branch
   * reproduces the old expression exactly; the flag-on branch adds the owner
   * term, matching how Layout's own `hasRole` has always treated owners.
   */
  const canManageAgents = useWorkspaceSource
    ? isAdminOrOwner || roles.includes('agents_manager')
    : globalRoles.some((r) => ['admin', 'agents_manager'].includes(r));

  /**
   * Legacy shape #4 — `Profile.tsx:345`:
   *   `ws.owner_id === user?.id || user?.roles?.includes('admin')`
   *
   * 🚨 The one expression that CANNOT move onto per-workspace roles. It runs
   * inside a loop over EVERY workspace the user belongs to, not the active one,
   * and the frontend holds no membership data for the non-active rows. The
   * `owner_id` half is already per-row and correct; the `admin` half stays on
   * the global payload by necessity. Post-Phase-0 that half becomes "admin of
   * the ACTIVE workspace", so a user who is admin of workspace A may see an
   * edit affordance on workspace B's row. Harmless — the rename endpoint
   * authorizes server-side and returns 403 — but it is a known cosmetic drift,
   * not an oversight. Fixing it properly needs a per-workspace role on the
   * workspace list payload.
   */
  const canEditWorkspace = (ws: Pick<Workspace, 'owner_id'>) =>
    ws.owner_id === user?.id || globalRoles.includes('admin');

  // 🚨 `permissionsReady` gates this, not the flag alone. A flag that is on
  // while the catalogue failed to load would evaluate `{}` as authoritative and
  // deny every page — the same confident-zero shape as an errored members
  // fetch. Not ready ⇒ fall back to the legacy role list, which is permissive
  // in exactly the way that keeps people working.
  const customRolesEnabled = user?.features?.rbac_custom_roles === true && permissionsReady;

  const canAccessNavItem = (item: NavItem) =>
    isNavItemVisible(item, {
      roles,
      isOwner,
      features: user?.features,
      permissions: rolePermissions,
      customRolesEnabled,
    });

  const can = (schema: string, verb: Verb): boolean => {
    if (isOwner || roles.includes('admin')) return true;
    if (customRolesEnabled) return getVerbSet(rolePermissions, schema)[verb];
    // Legacy era: there are no verbs, only page-level role lists. Fall back to
    // whether the member can see the page at all.
    const item = NAV_ITEMS.find((n) => n.schema === schema);
    if (!item) return false;
    return item.allowed.some((r) => roles.includes(r));
  };

  return {
    roles,
    isOwner,
    guardsEnabled,
    permsLoading,
    isAdminOrOwner,
    canAssignConversations,
    canManageAgents,
    canEditWorkspace,
    canAccessNavItem,
    customRolesEnabled,
    permissions: rolePermissions,
    can,
  };
};

export default usePermissions;
